import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getEnrollmentRequests, updateRequestStatus, exportEnrollmentRequestsCsv, getCourses,
} from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Inbox, Download, Filter, Search, Eye, CheckCircle2, XCircle, Clock, FileText, Globe, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { COUNTRIES, INDUSTRIES } from '../../utils/enrollmentOptions';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  pending: { label: 'Pendiente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Aprobada', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  rejected: { label: 'Rechazada', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const PAYMENT_LABEL = {
  paypal: 'PayPal',
  sinpe: 'Sinpe Móvil',
  nequi: 'Nequi',
  daviplata: 'Daviplata',
  mercadopago: 'Mercado Pago',
  yappy: 'Yappy',
  bank_transfer: 'Transferencia',
  offline: 'Pago Offline',
  scholarship: 'Beca',
  external: 'Link externo',
  other: 'Otro',
};

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const ManageEnrollmentRequests = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState({ course: '_all', status: '_all', country: '_all', industry: '_all' });
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (user && !['superadmin', 'admin', 'professor'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [reqs, c] = await Promise.all([getEnrollmentRequests(), getCourses()]);
      setRequests(reqs.data || []);
      setCourses(c.data || []);
    } catch {
      toast.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = ['superadmin', 'admin'].includes(user?.role);

  const filtered = useMemo(() => {
    return requests.filter(r => {
      if (filter.course !== '_all' && r.course_id !== filter.course) return false;
      if (filter.status !== '_all' && r.status !== filter.status) return false;
      if (filter.country !== '_all' && r.student_country !== filter.country) return false;
      if (filter.industry !== '_all' && r.industry !== filter.industry) return false;
      if (search) {
        const q = search.toLowerCase();
        const blob = `${r.student_name || ''} ${r.student_email || ''} ${r.organization || ''} ${r.job_title || ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [requests, filter, search]);

  const stats = useMemo(() => {
    const s = { total: filtered.length, pending: 0, approved: 0, rejected: 0 };
    filtered.forEach(r => { s[r.status] = (s[r.status] || 0) + 1; });
    return s;
  }, [filtered]);

  const changeStatus = async (id, newStatus) => {
    try {
      await updateRequestStatus(id, newStatus);
      toast.success(`Solicitud ${STATUS_LABEL[newStatus].label.toLowerCase()}`);
      setRequests(p => p.map(r => r.id === id ? { ...r, status: newStatus } : r));
      if (detail?.id === id) setDetail({ ...detail, status: newStatus });
    } catch {
      toast.error('Error al actualizar');
    }
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (filter.course !== '_all') params.course_id = filter.course;
      if (filter.status !== '_all') params.req_status = filter.status;
      if (filter.country !== '_all') params.country = filter.country;
      if (filter.industry !== '_all') params.industry = filter.industry;

      const res = await exportEnrollmentRequestsCsv(params);
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `solicitudes_inscripcion_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV descargado');
    } catch {
      toast.error('Error al exportar');
    }
  };

  const uniqCountries = useMemo(() => {
    const set = new Set(requests.map(r => r.student_country).filter(Boolean));
    return Array.from(set).sort();
  }, [requests]);

  const uniqIndustries = useMemo(() => {
    const set = new Set(requests.map(r => r.industry).filter(Boolean));
    return Array.from(set).sort();
  }, [requests]);

  if (loading) return <div className="p-8">Cargando solicitudes...</div>;

  return (
    <div className="p-6 md:p-12 bg-secondary min-h-screen" data-testid="manage-enrollments-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold mb-2">
              <Inbox className="w-3.5 h-3.5" /> CRM
            </div>
            <h1 className="text-3xl font-heading font-bold text-primary">Solicitudes de Inscripción</h1>
            <p className="text-muted-foreground">Revisa, filtra y exporta las solicitudes de tus cursos.</p>
          </div>
          <Button onClick={handleExport} data-testid="export-csv-btn" className="gap-2" disabled={!isAdmin}>
            <Download className="w-4 h-4" /> Exportar CSV
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { key: 'total', label: 'Total', color: 'text-primary' },
            { key: 'pending', label: 'Pendientes', color: 'text-amber-600' },
            { key: 'approved', label: 'Aprobadas', color: 'text-green-600' },
            { key: 'rejected', label: 'Rechazadas', color: 'text-red-600' },
          ].map(s => (
            <Card key={s.key}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">{s.label}</p>
                <p className={`text-3xl font-heading font-bold ${s.color}`}>{stats[s.key] || 0}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-semibold">Filtros</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="md:col-span-1">
                <Label className="text-xs">Buscar</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                  <Input
                    className="pl-8"
                    placeholder="Nombre, email, empresa..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    data-testid="search-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Curso</Label>
                <Select value={filter.course} onValueChange={v => setFilter(p => ({ ...p, course: v }))}>
                  <SelectTrigger data-testid="filter-course"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Todos los cursos</SelectItem>
                    {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Estado</Label>
                <Select value={filter.status} onValueChange={v => setFilter(p => ({ ...p, status: v }))}>
                  <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Todos</SelectItem>
                    <SelectItem value="pending">Pendientes</SelectItem>
                    <SelectItem value="approved">Aprobadas</SelectItem>
                    <SelectItem value="rejected">Rechazadas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">País</Label>
                <Select value={filter.country} onValueChange={v => setFilter(p => ({ ...p, country: v }))}>
                  <SelectTrigger data-testid="filter-country"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="_all">Todos los países</SelectItem>
                    {(uniqCountries.length ? uniqCountries : COUNTRIES).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Sector</Label>
                <Select value={filter.industry} onValueChange={v => setFilter(p => ({ ...p, industry: v }))}>
                  <SelectTrigger data-testid="filter-industry"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="_all">Todos los sectores</SelectItem>
                    {(uniqIndustries.length ? uniqIndustries : INDUSTRIES).map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* List */}
        {filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-muted-foreground">
              <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
              No hay solicitudes que coincidan con los filtros.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map(r => {
              const StatusIcon = STATUS_LABEL[r.status]?.icon || Clock;
              return (
                <Card key={r.id} className="hover:shadow-md transition-shadow" data-testid={`request-row-${r.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4 flex-wrap">
                      <div className="flex-1 min-w-[280px]">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="text-lg font-semibold">{r.student_name}</h3>
                          <Badge className={STATUS_LABEL[r.status]?.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {STATUS_LABEL[r.status]?.label || r.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{r.student_email}{r.student_phone ? ` · ${r.student_phone}` : ''}</p>
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                          <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" />{r.student_country || 'País N/A'}</span>
                          {r.job_title && <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" />{r.job_title}{r.organization ? ` · ${r.organization}` : ''}</span>}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          <strong>Curso:</strong> {r.course_title || '—'} · {fmt(r.created_at)}
                        </p>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {r.is_scholarship && (
                            <Badge className="bg-amber-100 text-amber-800 text-xs gap-1 border-amber-300" data-testid={`scholarship-badge-${r.id}`}>
                              🎓 BECA
                            </Badge>
                          )}
                          {r.payment_method && (
                            <Badge variant="outline" className="text-xs gap-1" data-testid={`payment-method-${r.id}`}>
                              <FileText className="w-3 h-3" />
                              {PAYMENT_LABEL[r.payment_method] || r.payment_method}
                            </Badge>
                          )}
                          {r.is_scholarship ? (
                            <Badge variant="outline" className="text-xs text-amber-700 gap-1 border-amber-300">
                              Sin pago (becado)
                            </Badge>
                          ) : r.payment_proof_url ? (
                            <Badge className="bg-green-100 text-green-700 text-xs gap-1" data-testid={`paid-yes-${r.id}`}>
                              <CheckCircle2 className="w-3 h-3" /> Comprobante adjunto
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs text-amber-600 gap-1" data-testid={`paid-no-${r.id}`}>
                              <Clock className="w-3 h-3" /> Sin comprobante
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline" onClick={() => setDetail(r)} data-testid={`view-request-${r.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          {detail?.id === r.id && (
                            <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle>Solicitud de {detail.student_name}</DialogTitle>
                              </DialogHeader>
                              <RequestDetailView req={detail} onChangeStatus={changeStatus} canManage={isAdmin} />
                            </DialogContent>
                          )}
                        </Dialog>
                        {isAdmin && r.status === 'pending' && (
                          <>
                            <Button size="sm" variant="outline" className="text-green-600 hover:bg-green-50"
                              onClick={() => changeStatus(r.id, 'approved')} data-testid={`approve-${r.id}`}>
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50"
                              onClick={() => changeStatus(r.id, 'rejected')} data-testid={`reject-${r.id}`}>
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value }) => value ? (
  <div>
    <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className="text-sm">{value}</p>
  </div>
) : null;

const RequestDetailView = ({ req, onChangeStatus, canManage = true }) => {
  const StatusIcon = STATUS_LABEL[req.status]?.icon || Clock;
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge className={STATUS_LABEL[req.status]?.color}>
          <StatusIcon className="w-3 h-3 mr-1" />{STATUS_LABEL[req.status]?.label || req.status}
        </Badge>
        <span className="text-xs text-muted-foreground">{fmt(req.created_at)}</span>
      </div>

      <section>
        <h4 className="font-semibold text-sm mb-2 text-primary">Datos personales</h4>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nombre" value={req.student_name} />
          <Field label="Email" value={req.student_email} />
          <Field label="Teléfono" value={req.student_phone} />
          <Field label="País" value={req.student_country} />
          <Field label="Ciudad" value={req.student_city} />
          <Field label="Documento" value={req.student_id_doc} />
          <Field label="Fecha nacimiento" value={req.student_birthdate} />
          <Field label="Género" value={req.student_gender} />
        </div>
      </section>

      <section>
        <h4 className="font-semibold text-sm mb-2 text-primary">Datos profesionales</h4>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Cargo" value={req.job_title} />
          <Field label="Organización" value={req.organization} />
          <Field label="Sector" value={req.industry} />
          <Field label="Experiencia" value={req.experience_years} />
          <Field label="Nivel educativo" value={req.education_level} />
          <Field label="LinkedIn" value={req.linkedin_url ? <a href={req.linkedin_url} target="_blank" rel="noreferrer" className="text-blue-600 underline break-all">{req.linkedin_url}</a> : null} />
          <Field label="Idioma" value={req.preferred_language} />
          <Field label="¿Cómo nos conoció?" value={req.referral_source} />
        </div>
      </section>

      <section>
        <h4 className="font-semibold text-sm mb-2 text-primary">Pago</h4>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Curso" value={req.course_title} />
          <Field label="Método de pago" value={req.payment_method ? (PAYMENT_LABEL[req.payment_method] || req.payment_method) : null} />
        </div>
        {req.payment_proof_url && (
          <div className="mt-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Comprobante</p>
            <a href={`${API_BASE}${req.payment_proof_url}`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="gap-2">
                <FileText className="w-4 h-4" /> Ver comprobante
              </Button>
            </a>
          </div>
        )}
      </section>

      {req.message && (
        <section>
          <h4 className="font-semibold text-sm mb-2 text-primary">Mensaje del estudiante</h4>
          <p className="text-sm bg-secondary p-3 rounded-lg whitespace-pre-line">{req.message}</p>
        </section>
      )}

      {req.status === 'pending' && canManage && (
        <div className="flex gap-2 pt-3 border-t">
          <Button onClick={() => onChangeStatus(req.id, 'approved')} className="bg-green-600 hover:bg-green-700 gap-2">
            <CheckCircle2 className="w-4 h-4" /> Aprobar
          </Button>
          <Button variant="outline" onClick={() => onChangeStatus(req.id, 'rejected')} className="text-red-600 gap-2">
            <XCircle className="w-4 h-4" /> Rechazar
          </Button>
        </div>
      )}
    </div>
  );
};

export default ManageEnrollmentRequests;
